import {
  type CreateTenantInput,
  CreateTenantInputSchema,
  SlugSchema,
  type Tenant,
  type UpdateTenantInput,
  UpdateTenantInputSchema,
} from '@sazeo/schemas';
import { getTenantClient } from '@sazeo/supabase-tenant';
import { type CoreError, coreError } from './errors';

/**
 * Resolve a tenant by slug. Used by the tenant-app root loader to map
 * `{slug}.app.sazeo.io` → tenant record.
 *
 * RLS: user only sees rows where they hold a membership. For unauthenticated
 * requests (marketing pages, public profiles) use a dedicated public-read
 * endpoint instead; this helper is for member contexts.
 */
export const getTenantBySlug = async (slug: string): Promise<Tenant | CoreError> => {
  const parsed = SlugSchema.safeParse(slug);
  if (!parsed.success) {
    return coreError('VALIDATION', 'Invalid slug', { issues: parsed.error.issues });
  }

  const client = getTenantClient();
  const { data, error } = await client
    .from('tenants')
    .select('id, slug, name, logo_url, status, plan_id, created_at, updated_at')
    .eq('slug', parsed.data)
    .maybeSingle();

  if (error) return coreError('INTERNAL', error.message);
  if (!data) return coreError('NOT_FOUND', `Tenant "${slug}" not found`);
  return data as Tenant;
};

/**
 * Create a new tenant (workspace). Called from the signup/workspace flow.
 *
 * Server-side only — this path writes to the `tenants` table, which has no
 * INSERT policy for regular authenticated users. Callers must invoke this
 * from an Edge Function that holds `service_role`, or via an RPC that is
 * `SECURITY DEFINER`. This helper assumes the caller has arranged either.
 *
 * Wave 3 will move this onto a dedicated `functions/app/create-workspace`
 * Edge Function so the client can call it over HTTPS instead of reaching
 * directly into service_role keys.
 */
export const createTenant = async (
  input: CreateTenantInput,
  opts: { ownerUserId: string },
): Promise<Tenant | CoreError> => {
  const parsed = CreateTenantInputSchema.safeParse(input);
  if (!parsed.success) {
    return coreError('VALIDATION', 'Invalid input', { issues: parsed.error.issues });
  }

  const client = getTenantClient();

  const existing = await client
    .from('tenants')
    .select('id')
    .eq('slug', parsed.data.slug)
    .maybeSingle();
  if (existing.data) {
    return coreError('CONFLICT', `Slug "${parsed.data.slug}" already taken`);
  }

  const insert = await client
    .from('tenants')
    .insert({
      slug: parsed.data.slug,
      name: parsed.data.name,
      logo_url: parsed.data.logo_url ?? null,
    })
    .select()
    .single();

  if (insert.error) return coreError('INTERNAL', insert.error.message);

  const ownerRole = await client
    .from('roles')
    .select('id')
    .eq('is_system', true)
    .eq('name', 'owner')
    .maybeSingle();

  if (ownerRole.error || !ownerRole.data) {
    return coreError('INTERNAL', 'System role "owner" missing — seed migration 0001');
  }

  const membership = await client.from('memberships').insert({
    user_id: opts.ownerUserId,
    tenant_id: insert.data.id,
    role_id: ownerRole.data.id,
  });

  if (membership.error) return coreError('INTERNAL', membership.error.message);

  return insert.data as Tenant;
};

/**
 * Update mutable tenant fields. Admin-only at policy layer.
 */
export const updateTenant = async (
  tenantId: string,
  input: UpdateTenantInput,
): Promise<Tenant | CoreError> => {
  const parsed = UpdateTenantInputSchema.safeParse(input);
  if (!parsed.success) {
    return coreError('VALIDATION', 'Invalid input', { issues: parsed.error.issues });
  }

  const client = getTenantClient();
  const { data, error } = await client
    .from('tenants')
    .update(parsed.data)
    .eq('id', tenantId)
    .select()
    .single();

  if (error) return coreError('INTERNAL', error.message);
  if (!data) return coreError('NOT_FOUND', 'Tenant not found or no permission');
  return data as Tenant;
};

/**
 * List every tenant the current user holds a membership in — used by the
 * app.sazeo.io workspace chooser.
 */
export const listUserTenants = async (userId: string): Promise<Tenant[] | CoreError> => {
  const client = getTenantClient();
  const { data, error } = await client
    .from('memberships')
    .select('tenant:tenants(id, slug, name, logo_url, status, plan_id, created_at, updated_at)')
    .eq('user_id', userId);

  if (error) return coreError('INTERNAL', error.message);

  type MembershipTenantRow = { tenant: Tenant | Tenant[] | null };
  const tenants = (data ?? [])
    .map((row) => {
      const tenant = (row as MembershipTenantRow).tenant;
      return Array.isArray(tenant) ? (tenant[0] ?? null) : tenant;
    })
    .filter((t): t is Tenant => t !== null && t.status !== 'archived');

  return tenants;
};
